import React, { useState, useEffect, useMemo } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { API } from "../App";
import {
  BarChart3, FolderLock, AlertTriangle, Clock, RefreshCw,
  FileText, ChevronRight, Hourglass, Zap, TrendingUp, TrendingDown,
  Target, Minus,
} from "lucide-react";

const STATUS_LABEL = {
  open: "Open",
  monitoring: "Monitoring",
  steward_review: "Steward Review",
  grievance_drafted: "Grievance Drafted",
  closed: "Closed",
};

const STATUS_COLOR = {
  open: "#22c55e",
  monitoring: "#f59e0b",
  steward_review: "#60a5fa",
  grievance_drafted: "#a78bfa",
  closed: "#71717a",
};

function pct(n, max) {
  if (!max) return 0;
  return Math.max(2, Math.round((n / max) * 100));
}

function daysAgo(iso) {
  if (!iso) return "—";
  const then = new Date(iso).getTime();
  const diffDays = Math.floor((Date.now() - then) / (1000 * 60 * 60 * 24));
  if (diffDays < 1) return "today";
  if (diffDays === 1) return "1 day ago";
  return `${diffDays} days ago`;
}

function formatIssue(v) {
  return (v || "—").replace(/_/g, " ");
}

/* ── SUMMARY TILES ───────────────────────────────────────────── */
function Tile({ icon: Icon, label, value, accent, testId }) {
  return (
    <div className={`ca-tile ca-accent-${accent}`} data-testid={testId}>
      <div className="ca-tile-head">
        <Icon size={14} />
        <span>{label}</span>
      </div>
      <div className="ca-tile-value">{value}</div>
    </div>
  );
}

/* ── GROUP BAR (by issue / by status) ────────────────────────── */
function GroupTable({ rows, labelKey, emptyText, colorByLabel, onRowClick, testId }) {
  const max = useMemo(() => rows.reduce((m, r) => Math.max(m, r.count || 0), 0), [rows]);
  if (!rows.length) {
    return <div className="ca-empty" data-testid={`${testId}-empty`}>{emptyText}</div>;
  }
  return (
    <div className="ca-group" data-testid={testId}>
      {rows.map((r, i) => {
        const raw = r[labelKey];
        const label = labelKey === "status"
          ? (STATUS_LABEL[raw] || formatIssue(raw))
          : formatIssue(raw);
        const dot = colorByLabel ? (STATUS_COLOR[raw] || "#a78bfa") : "#a78bfa";
        return (
          <button
            key={i}
            type="button"
            className="ca-group-row"
            onClick={() => onRowClick && raw ? onRowClick(raw) : null}
            disabled={!onRowClick || !raw}
            data-testid={`${testId}-row-${raw || "unknown"}`}
          >
            <div className="ca-group-label">
              <span className="ca-dot" style={{ background: dot }} />
              {label}
            </div>
            <div className="ca-group-bar">
              <div className="ca-group-bar-fill" style={{ width: `${pct(r.count, max)}%`, background: dot }} />
            </div>
            <div className="ca-group-count">{r.count}</div>
          </button>
        );
      })}
    </div>
  );
}

/* ── CASE LIST (stale + recent) ──────────────────────────────── */
function CaseList({ rows, emptyText, onOpen, testId, showAge = false }) {
  if (!rows.length) return <div className="ca-empty" data-testid={`${testId}-empty`}>{emptyText}</div>;
  return (
    <div className="ca-list" data-testid={testId}>
      {rows.map(c => (
        <button
          key={c.case_id}
          type="button"
          className="ca-list-row"
          onClick={() => onOpen(c.case_id)}
          data-testid={`${testId}-row-${c.case_id}`}
        >
          <div className="ca-list-id">{c.case_id}</div>
          <div className="ca-list-main">
            <div className="ca-list-title">{c.title || "(untitled)"}</div>
            <div className="ca-list-meta">
              <span className="ca-chip">{formatIssue(c.issue_type)}</span>
              <span className="ca-chip" style={{ color: STATUS_COLOR[c.status], borderColor: STATUS_COLOR[c.status] + "44" }}>
                {STATUS_LABEL[c.status] || c.status}
              </span>
              {showAge && (
                <span className="ca-chip ca-chip-age">
                  <Clock size={10} /> last activity {daysAgo(c.last_activity_at)}
                </span>
              )}
            </div>
          </div>
          <ChevronRight size={14} className="ca-list-arrow" />
        </button>
      ))}
    </div>
  );
}

/* ── ISSUE INTELLIGENCE ──────────────────────────────────────── */
const SIGNAL_META = {
  healthy_engagement: {
    label: "Healthy engagement",
    hint: "Bank resonates — keep refining in place.",
    color: "#22c55e", icon: TrendingUp,
  },
  bank_expansion_needed: {
    label: "Bank expansion needed",
    hint: "Real cases, low pill engagement — add trade/context questions.",
    color: "#f59e0b", icon: Target,
  },
  engagement_without_cases: {
    label: "Engagement without cases",
    hint: "Clicks without case outcomes — pills may not be nudging action.",
    color: "#60a5fa", icon: TrendingDown,
  },
  low_activity: {
    label: "Low activity",
    hint: "Not much happening here — monitor before investing.",
    color: "#71717a", icon: Clock,
  },
};

const TREND_META = {
  up:   { color: "#22c55e", icon: TrendingUp,   label: "trending up" },
  down: { color: "#ef4444", icon: TrendingDown, label: "trending down" },
  flat: { color: "#71717a", icon: Minus,        label: "flat" },
};

/* Inline 7-bar SVG sparkline. Each bar = one day (oldest → newest). */
function Sparkline({ values = [], color = "#a78bfa" }) {
  const safe = values.length === 7 ? values : [0, 0, 0, 0, 0, 0, 0];
  const max = Math.max(1, ...safe); // avoid div-by-zero
  const W = 84;
  const H = 22;
  const barW = 9;
  const gap = (W - barW * 7) / 6;
  return (
    <svg
      width={W}
      height={H}
      viewBox={`0 0 ${W} ${H}`}
      role="img"
      aria-label={`7-day clicks: ${safe.join(", ")}`}
      data-testid="ii-sparkline"
    >
      {safe.map((v, i) => {
        const h = Math.round((v / max) * (H - 2)) || 1; // 1px floor for zero
        return (
          <rect
            key={i}
            x={i * (barW + gap)}
            y={H - h}
            width={barW}
            height={h}
            rx={1}
            fill={v > 0 ? color : "rgba(255,255,255,0.08)"}
          />
        );
      })}
    </svg>
  );
}

function IssueIntelligenceRow({ row }) {
  const meta = SIGNAL_META[row.signal] || SIGNAL_META.low_activity;
  const Icon = meta.icon;
  const trend = TREND_META[row.trend] || TREND_META.flat;
  const TrendIcon = trend.icon;
  const sparkValues = row.daily_clicks_7d || [];
  return (
    <div className="ca-ii-row" data-testid={`ii-row-${row.canonical_issue}`}>
      <div className="ca-ii-head">
        <div className="ca-ii-name">{formatIssue(row.canonical_issue)}</div>
        <div
          className="ca-ii-signal"
          data-testid={`ii-signal-${row.canonical_issue}`}
          style={{ color: meta.color, borderColor: meta.color + "55", background: meta.color + "14" }}
        >
          <Icon size={11} /> {meta.label}
        </div>
      </div>
      <div className="ca-ii-metrics">
        <div className="ca-ii-metric">
          <FolderLock size={11} /> <b>{row.case_count}</b> case{row.case_count === 1 ? "" : "s"}
        </div>
        <div className="ca-ii-metric">
          <Zap size={11} /> <b>{row.click_count}</b> pill click{row.click_count === 1 ? "" : "s"}
        </div>
      </div>

      {/* Sparkline + 7d/30d/trend (only show when we have meaningful data) */}
      {(row.clicks_30d > 0 || row.clicks_7d > 0) && (
        <div
          className="ca-ii-spark"
          data-testid={`ii-spark-${row.canonical_issue}`}
        >
          <Sparkline values={sparkValues} color={meta.color} />
          <div className="ca-ii-spark-stats">
            <span>
              <b data-testid={`ii-clicks7d-${row.canonical_issue}`}>{row.clicks_7d}</b>
              <span className="ca-ii-spark-sub"> 7d</span>
            </span>
            <span>
              <b data-testid={`ii-clicks30d-${row.canonical_issue}`}>{row.clicks_30d}</b>
              <span className="ca-ii-spark-sub"> 30d</span>
            </span>
            <span
              className="ca-ii-trend"
              style={{ color: trend.color }}
              data-testid={`ii-trend-${row.canonical_issue}`}
              title={trend.label}
            >
              <TrendIcon size={12} />
            </span>
          </div>
        </div>
      )}

      <div className="ca-ii-hint">{meta.hint}</div>
      {row.top_pills?.length > 0 && (
        <div className="ca-ii-pills" data-testid={`ii-pills-${row.canonical_issue}`}>
          <div className="ca-ii-pills-label">Top clicked:</div>
          {row.top_pills.map((p, i) => (
            <div key={p.pill_id || i} className="ca-ii-pill">
              <span className="ca-ii-pill-count">{p.count}×</span>
              <span className="ca-ii-pill-text">{p.pill_text}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/* ── MAIN PAGE ───────────────────────────────────────────────── */
export default function CaseAnalyticsPage() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [staleDays, setStaleDays] = useState(14);

  async function load() {
    setLoading(true);
    setErr("");
    try {
      const res = await axios.get(
        `${API}/case-locker/analytics?stale_days=${staleDays}&recent_limit=10`
      );
      setData(res.data);
    } catch {
      setErr("Failed to load case analytics.");
    } finally {
      setLoading(false);
    }
  }
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [staleDays]);

  const totals = {
    total: data?.total_cases ?? 0,
    avgDays: data?.avg_time_to_grievance_days ?? null,
    grievanceN: data?.grievance_sample_size ?? 0,
    staleCount: data?.stale_cases?.length ?? 0,
  };

  const topIssue = data?.cases_by_issue_type?.[0]?.issue_type || "—";

  return (
    <div className="ca-page" data-testid="case-analytics-page">
      <style>{caStyles}</style>

      <div className="ca-header">
        <div>
          <h1 className="ca-title">Case Locker Analytics</h1>
          <p className="ca-sub">How your cases are flowing, where they're stalling, and the mix of work.</p>
        </div>
        <div className="ca-header-controls">
          <label className="ca-stale-label">
            <Hourglass size={12} /> Stale threshold:
            <select
              className="ca-select"
              data-testid="stale-threshold-select"
              value={staleDays}
              onChange={(e) => setStaleDays(parseInt(e.target.value, 10))}
            >
              <option value={7}>7 days</option>
              <option value={14}>14 days</option>
              <option value={30}>30 days</option>
              <option value={60}>60 days</option>
            </select>
          </label>
          <button
            type="button"
            className="ca-reload"
            onClick={load}
            data-testid="reload-btn"
            disabled={loading}
          >
            <RefreshCw size={13} className={loading ? "ca-spin" : ""} />
            {loading ? "Loading…" : "Refresh"}
          </button>
        </div>
      </div>

      {err && (
        <div className="ca-error" data-testid="ca-error">
          <AlertTriangle size={14} /> {err}
        </div>
      )}

      {/* P68: Health alerts — surface any cases stuck in the 'other' bucket etc. */}
      {Array.isArray(data?.health_alerts) && data.health_alerts.length > 0 && (
        <div className="ca-health-alerts" data-testid="ca-health-alerts">
          {data.health_alerts.map((a) => (
            <div
              key={a.code}
              className={`ca-health-alert ca-health-${a.level || "warning"}`}
              data-testid={`ca-health-alert-${a.code}`}
            >
              <AlertTriangle size={14} />
              <div>
                <div className="ca-health-title">{a.title}</div>
                <div className="ca-health-msg">{a.message}</div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Tiles */}
      <div className="ca-tiles">
        <Tile icon={FolderLock} label="TOTAL CASES" value={totals.total} accent="violet" testId="tile-total" />
        <Tile
          icon={Clock}
          label="AVG TIME TO GRIEVANCE"
          value={
            totals.avgDays != null
              ? `${totals.avgDays} day${totals.avgDays === 1 ? "" : "s"}`
              : "—"
          }
          accent="cyan"
          testId="tile-avg-days"
        />
        <Tile
          icon={FileText}
          label="GRIEVANCES FILED"
          value={totals.grievanceN}
          accent="green"
          testId="tile-grievances"
        />
        <Tile
          icon={AlertTriangle}
          label={`STALE (>${data?.stale_days_threshold ?? staleDays}D)`}
          value={totals.staleCount}
          accent="amber"
          testId="tile-stale"
        />
        <Tile
          icon={BarChart3}
          label="TOP ISSUE"
          value={formatIssue(topIssue)}
          accent="indigo"
          testId="tile-top-issue"
        />
      </div>

      {/* Two-column distributions */}
      <div className="ca-split">
        <section className="ca-section" data-testid="section-by-issue">
          <h2 className="ca-section-title">Cases by Issue Type</h2>
          <GroupTable
            rows={data?.cases_by_issue_type || []}
            labelKey="issue_type"
            emptyText="No cases yet."
            onRowClick={(v) => navigate(`/case-locker?issue_type=${encodeURIComponent(v)}`)}
            testId="by-issue-table"
          />
        </section>
        <section className="ca-section" data-testid="section-by-status">
          <h2 className="ca-section-title">Cases by Status</h2>
          <GroupTable
            rows={data?.cases_by_status || []}
            labelKey="status"
            emptyText="No cases yet."
            colorByLabel
            onRowClick={(v) => navigate(`/case-locker?status=${encodeURIComponent(v)}`)}
            testId="by-status-table"
          />
        </section>
      </div>

      {/* Issue intelligence: case + click cross-reference */}
      <section className="ca-section" data-testid="section-issue-intelligence">
        <h2 className="ca-section-title">
          Issue Intelligence
          <span className="ca-section-hint-inline">
            cases × follow-up clicks — where to invest in the question banks
          </span>
        </h2>
        {data?.issue_intelligence?.length ? (
          <div className="ca-ii-grid">
            {data.issue_intelligence.map(r => (
              <IssueIntelligenceRow key={r.canonical_issue} row={r} />
            ))}
          </div>
        ) : (
          <div className="ca-empty" data-testid="ii-empty">
            Not enough data to produce issue intelligence yet.
          </div>
        )}
      </section>

      {/* Stale cases */}
      <section className="ca-section" data-testid="section-stale">
        <h2 className="ca-section-title">
          Stale Cases
          <span className="ca-section-hint-inline">
            no activity in {data?.stale_days_threshold ?? staleDays} days
          </span>
        </h2>
        <CaseList
          rows={data?.stale_cases || []}
          emptyText="No stale cases. 🎉"
          onOpen={(id) => navigate(`/case-locker/${id}`)}
          testId="stale-list"
          showAge
        />
      </section>

      {/* Recent cases */}
      <section className="ca-section" data-testid="section-recent">
        <h2 className="ca-section-title">Recent Cases</h2>
        <CaseList
          rows={data?.recent_cases || []}
          emptyText="No recent cases."
          onOpen={(id) => navigate(`/case-locker/${id}`)}
          testId="recent-list"
          showAge
        />
      </section>
    </div>
  );
}

const caStyles = `
.ca-page {
  max-width: 1100px;
  margin: 0 auto;
  padding: 28px 20px 80px;
  color: #e4e4e7;
  font-family: 'Inter', -apple-system, sans-serif;
}
.ca-header {
  display: flex; align-items: flex-start; justify-content: space-between;
  gap: 16px; margin-bottom: 20px; flex-wrap: wrap;
}
.ca-title { font-size: 22px; font-weight: 800; color: #fafafa; margin: 0 0 4px; letter-spacing: -0.01em; }
.ca-sub { font-size: 13px; color: #a1a1aa; margin: 0; }

.ca-header-controls { display: flex; align-items: center; gap: 8px; }
.ca-stale-label {
  display: inline-flex; align-items: center; gap: 6px;
  font-size: 11px; font-weight: 700; color: #a1a1aa; letter-spacing: 0.05em;
}
.ca-select {
  padding: 5px 10px; border-radius: 6px;
  background: rgba(255,255,255,0.04);
  border: 1px solid rgba(255,255,255,0.08);
  color: #e5e5e5; font-size: 12px; font-family: inherit;
}

.ca-reload {
  display: flex; align-items: center; gap: 6px;
  padding: 7px 14px; border-radius: 8px;
  background: rgba(139,92,246,0.1);
  border: 1px solid rgba(139,92,246,0.25);
  color: #c4b5fd; font-size: 12px; font-weight: 600; cursor: pointer;
  font-family: inherit;
}
.ca-reload:hover:not(:disabled) { background: rgba(139,92,246,0.18); border-color: rgba(139,92,246,0.45); }
.ca-reload:disabled { opacity: 0.6; cursor: default; }
.ca-spin { animation: ca-spin 0.9s linear infinite; }
@keyframes ca-spin { to { transform: rotate(360deg); } }

.ca-error {
  display: flex; align-items: center; gap: 8px;
  padding: 10px 14px; border-radius: 10px;
  background: rgba(239,68,68,0.08); border: 1px solid rgba(239,68,68,0.25);
  color: #fca5a5; font-size: 13px; font-weight: 600;
  margin-bottom: 18px;
}

/* P68: Health-alert banners (e.g. "other" bucket nonzero) */
.ca-health-alerts {
  display: flex; flex-direction: column; gap: 8px;
  margin-bottom: 18px;
}
.ca-health-alert {
  display: flex; align-items: flex-start; gap: 10px;
  padding: 12px 14px; border-radius: 10px;
  font-size: 12px; line-height: 1.45;
}
.ca-health-alert > svg { flex-shrink: 0; margin-top: 2px; }
.ca-health-warning {
  background: rgba(245,158,11,0.06);
  border: 1px solid rgba(245,158,11,0.25);
  color: #fcd34d;
}
.ca-health-warning > svg { color: #f59e0b; }
.ca-health-title {
  font-weight: 800; color: #fef3c7; margin-bottom: 2px;
}
.ca-health-msg { color: #d4d4d8; font-size: 12px; font-family: 'SF Mono', monospace; }

/* Tiles */
.ca-tiles {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(195px, 1fr));
  gap: 12px; margin-bottom: 28px;
}
.ca-tile {
  padding: 16px 18px; border-radius: 14px;
  background: rgba(24,24,27,0.6);
  border: 1px solid rgba(255,255,255,0.06);
}
.ca-tile-head {
  display: flex; align-items: center; gap: 8px;
  font-size: 10px; font-weight: 800; text-transform: uppercase;
  letter-spacing: 0.09em; color: #71717a;
  margin-bottom: 10px;
}
.ca-tile-value {
  font-size: 24px; font-weight: 800; color: #fafafa;
  letter-spacing: -0.02em;
}
.ca-accent-violet { border-color: rgba(139,92,246,0.18); background: linear-gradient(135deg, rgba(139,92,246,0.08), rgba(24,24,27,0.6)); }
.ca-accent-violet .ca-tile-head svg { color: #a78bfa; }
.ca-accent-cyan   { border-color: rgba(6,182,212,0.18); background: linear-gradient(135deg, rgba(6,182,212,0.08), rgba(24,24,27,0.6)); }
.ca-accent-cyan .ca-tile-head svg { color: #67e8f9; }
.ca-accent-green  { border-color: rgba(16,185,129,0.18); background: linear-gradient(135deg, rgba(16,185,129,0.08), rgba(24,24,27,0.6)); }
.ca-accent-green .ca-tile-head svg { color: #34d399; }
.ca-accent-amber  { border-color: rgba(245,158,11,0.2); background: linear-gradient(135deg, rgba(245,158,11,0.08), rgba(24,24,27,0.6)); }
.ca-accent-amber .ca-tile-head svg { color: #fbbf24; }
.ca-accent-indigo { border-color: rgba(99,102,241,0.2); background: linear-gradient(135deg, rgba(99,102,241,0.08), rgba(24,24,27,0.6)); }
.ca-accent-indigo .ca-tile-head svg { color: #a5b4fc; }

/* Sections */
.ca-split {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(320px, 1fr));
  gap: 14px; margin-bottom: 14px;
}
.ca-section {
  margin-bottom: 26px;
  padding: 18px 18px 20px;
  border-radius: 14px;
  background: rgba(15,15,18,0.9);
  border: 1px solid rgba(255,255,255,0.05);
}
.ca-section-title {
  margin: 0 0 12px; font-size: 13px; font-weight: 800;
  color: #fafafa; text-transform: uppercase; letter-spacing: 0.06em;
  display: flex; align-items: center; gap: 10px;
}
.ca-section-hint-inline {
  font-size: 10px; color: #71717a; font-weight: 500; letter-spacing: 0.05em;
  text-transform: lowercase;
}

/* Group rows */
.ca-group { display: flex; flex-direction: column; gap: 6px; }
.ca-group-row {
  display: grid;
  grid-template-columns: 190px 1fr 40px;
  gap: 12px; align-items: center;
  padding: 8px 10px; border-radius: 8px;
  background: rgba(24,24,27,0.5);
  border: 1px solid rgba(255,255,255,0.04);
  text-align: left;
  font-family: inherit; cursor: pointer;
  transition: background 0.12s, border-color 0.12s;
}
.ca-group-row:hover:not(:disabled) {
  background: rgba(255,255,255,0.05); border-color: rgba(139,92,246,0.24);
}
.ca-group-row:disabled { cursor: default; }
.ca-group-label {
  display: inline-flex; align-items: center; gap: 7px;
  color: #e4e4e7; font-weight: 600; font-size: 12px;
  text-transform: capitalize;
}
.ca-dot {
  width: 8px; height: 8px; border-radius: 50%;
  display: inline-block; flex-shrink: 0;
}
.ca-group-bar {
  height: 6px; border-radius: 99px;
  background: rgba(255,255,255,0.04); overflow: hidden;
}
.ca-group-bar-fill { height: 100%; border-radius: 99px; transition: width 0.25s; }
.ca-group-count {
  font-weight: 800; color: #fafafa; text-align: right;
  font-variant-numeric: tabular-nums; font-size: 13px;
}

/* Case list */
.ca-list { display: flex; flex-direction: column; gap: 6px; }
.ca-list-row {
  display: grid;
  grid-template-columns: 120px 1fr 16px;
  gap: 10px; align-items: center;
  padding: 10px 12px; border-radius: 10px;
  background: rgba(24,24,27,0.5);
  border: 1px solid rgba(255,255,255,0.04);
  text-align: left;
  cursor: pointer; font-family: inherit;
  transition: background 0.12s, border-color 0.12s;
}
.ca-list-row:hover {
  background: rgba(255,255,255,0.05); border-color: rgba(139,92,246,0.24);
}
.ca-list-id { font-size: 10px; font-family: 'SF Mono', monospace; color: #a1a1aa; }
.ca-list-title { font-size: 13px; font-weight: 600; color: #e5e5e5; margin-bottom: 5px; line-height: 1.3; }
.ca-list-meta { display: flex; flex-wrap: wrap; gap: 5px; }
.ca-list-arrow { color: #525252; flex-shrink: 0; }

.ca-chip {
  display: inline-flex; align-items: center; gap: 4px;
  padding: 2px 7px; border-radius: 4px;
  font-size: 10px; font-weight: 700;
  background: rgba(139,92,246,0.08);
  border: 1px solid rgba(139,92,246,0.18);
  color: #c4b5fd; text-transform: capitalize;
  font-family: inherit;
}
.ca-chip-age {
  background: rgba(245,158,11,0.06);
  border-color: rgba(245,158,11,0.2);
  color: #fbbf24;
}

/* Issue Intelligence */
.ca-ii-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(310px, 1fr));
  gap: 10px;
}
.ca-ii-row {
  padding: 14px 14px 12px;
  border-radius: 12px;
  background: rgba(24,24,27,0.55);
  border: 1px solid rgba(255,255,255,0.04);
}
.ca-ii-head {
  display: flex; justify-content: space-between; align-items: flex-start;
  gap: 10px; margin-bottom: 10px;
}
.ca-ii-name {
  font-size: 13px; font-weight: 800; color: #fafafa;
  text-transform: capitalize; letter-spacing: -0.01em;
}
.ca-ii-signal {
  display: inline-flex; align-items: center; gap: 5px;
  padding: 3px 8px; border-radius: 99px;
  border: 1px solid;
  font-size: 10px; font-weight: 700;
  letter-spacing: 0.02em; white-space: nowrap;
}
.ca-ii-metrics {
  display: flex; gap: 14px; margin-bottom: 8px;
  font-size: 12px; color: #a1a1aa;
}
.ca-ii-metric { display: inline-flex; align-items: center; gap: 5px; }
.ca-ii-metric b { color: #fafafa; font-size: 14px; font-weight: 800; font-variant-numeric: tabular-nums; }

/* Sparkline strip (7d / 30d / trend) */
.ca-ii-spark {
  display: flex; align-items: center; gap: 12px;
  padding: 8px 0 6px;
  border-top: 1px solid rgba(255,255,255,0.03);
  margin-bottom: 4px;
}
.ca-ii-spark svg { display: block; flex-shrink: 0; }
.ca-ii-spark-stats {
  display: flex; align-items: center; gap: 10px;
  font-size: 11px; color: #d4d4d8;
  font-variant-numeric: tabular-nums;
}
.ca-ii-spark-stats b { color: #fafafa; font-weight: 800; font-size: 12px; }
.ca-ii-spark-sub { color: #71717a; font-weight: 600; font-size: 10px; letter-spacing: 0.04em; }
.ca-ii-trend {
  display: inline-flex; align-items: center;
  padding: 2px 6px; border-radius: 99px;
  background: rgba(255,255,255,0.02);
}
.ca-ii-hint {
  font-size: 11px; color: #71717a; line-height: 1.5;
  padding-top: 6px; border-top: 1px solid rgba(255,255,255,0.03);
}
.ca-ii-pills {
  margin-top: 10px;
  padding-top: 10px;
  border-top: 1px solid rgba(255,255,255,0.03);
  font-size: 11px;
}
.ca-ii-pills-label {
  font-size: 9px; font-weight: 800; letter-spacing: 0.08em;
  text-transform: uppercase; color: #71717a; margin-bottom: 6px;
}
.ca-ii-pill {
  display: flex; align-items: flex-start; gap: 6px;
  padding: 4px 0;
  color: #d4d4d8; font-size: 11px; line-height: 1.35;
}
.ca-ii-pill-count {
  color: #a78bfa; font-weight: 800;
  font-family: 'SF Mono', monospace;
  font-size: 10px; min-width: 26px;
}
.ca-ii-pill-text { flex: 1; }

.ca-empty {
  padding: 18px; text-align: center;
  font-size: 13px; color: #71717a; font-style: italic;
  border: 1px dashed rgba(255,255,255,0.06);
  border-radius: 10px;
}

@media (max-width: 640px) {
  .ca-group-row { grid-template-columns: 120px 1fr 36px; }
  .ca-list-row  { grid-template-columns: 88px 1fr 14px; }
}
`;
